import type {ResolverFamily} from '@/services/processorService';
import type {AfriexCurrency, AfriexTransactionProcessor} from '@/types';

export type {ResolverFamily};

export type UpdateResolversRequest = {
  currency: AfriexCurrency;
  /**
   * The full desired state, not a delta. An absent family means "not
   * configured": the key is dropped rather than stored as an empty string,
   * which is what the server reads as unset.
   */
  resolvers: Partial<Record<ResolverFamily, AfriexTransactionProcessor>>;
};

/**
 * The four families, with the label and the explanation each one needs.
 *
 * `virtual` is absent because retail resolves virtual-account providers from
 * the VIRTUAL_BANK_ACCOUNT channel slot, which the Corridors screen edits.
 *
 * `card` was previously absent because it had an unmigrated B2B twin
 * (`checkoutSession.cardProcessor`). Both surfaces read
 * `params.resolvers.card` now, so one value here decides the vault for retail
 * and checkout-web alike.
 */
export const RESOLVER_FAMILIES: {
  key: ResolverFamily;
  label: string;
  hint: string;
}[] = [
  {
    key: 'bank',
    label: 'Bank name',
    hint: 'Provider that looks up an account holder’s name for bank transfers.',
  },
  {
    key: 'mobile',
    label: 'Mobile money name',
    hint: 'Provider that looks up a wallet holder’s name. Unset falls back to the global default.',
  },
  {
    key: 'ach',
    label: 'ACH creation',
    hint: 'Processor an ACH bank account is created with. Not the deposit routing processor.',
  },
  {
    key: 'card',
    label: 'Card vault',
    hint: 'Vault a card is tokenised into, for both the app and checkout. Not the deposit routing processor.',
  },
];

/**
 * Does this currency name a resolver for any family?
 *
 * The row set is every currency document (64 of them), but only a handful
 * carry a resolver, so this is what separates "a resolver someone manages"
 * from "a currency that merely exists". Derived from the data rather than a
 * list of interesting currencies, so a newly configured one appears without a
 * code change. Mirrors `hasConfiguredCorridor` on the Corridors screen.
 */
export const hasConfiguredResolver = (
  currency: AfriexCurrency | undefined,
): boolean =>
  RESOLVER_FAMILIES.some(({key}) =>
    Boolean(currency?.params?.resolvers?.[key]),
  );
